/**
 * Nhường main thread cho trình duyệt vẽ phản hồi TRƯỚC khi chạy điều hướng SPA.
 *
 * Vì sao: react-router v5 + redux-first-history đẩy `history.push` → `LOCATION_CHANGE`
 * → render + commit trang mới NGAY TRONG lượt dispatch của cú click (SyncLane).
 * react-redux 8 dùng useSyncExternalStore nên `startTransition` không giải quyết được.
 *
 * Cách làm: chỉ khi push/replace được gọi TRONG một sự kiện người dùng thật
 * (click, phím, pointerup, submit…), hoãn nó qua frame kế tiếp:
 * requestAnimationFrame chạy ngay trước khi vẽ, setTimeout(0) bên trong nó chạy SAU
 * khi frame đó đã được vẽ ⇒ trình duyệt vẽ phản hồi của cú click trước, rồi mới
 * render trang mới trong một task riêng.
 *
 * ⚠️ Không giảm tổng khối lượng việc — chỉ đổi thứ tự. Việc render nặng vẫn còn,
 * chỉ không nằm trong cửa sổ INP của cú click nữa. Muốn giảm việc thật phải sửa
 * remount layout/trang (xem docs/perf).
 *
 * ⚠️ Gọi ngoài sự kiện người dùng (saga, effect, timer) vẫn chạy ĐỒNG BỘ như cũ,
 * để code đọc `location` ngay sau khi push không bị đổi hành vi.
 * ⚠️ push/replace cùng URL (chỉ đổi state) cũng chạy đồng bộ: xem isSameUrl.
 * ⚠️ Trong một cú bấm, code gọi push rồi đọc `history.location` ngay sau đó sẽ thấy URL CŨ.
 */

type NavigateFn = (...args: any[]) => void;

interface NavigableHistory {
  push: NavigateFn;
  replace: NavigateFn;
}

// Các sự kiện có thể là cú tương tác được tính vào INP.
const USER_EVENT_TYPES = new Set(['click', 'auxclick', 'pointerup', 'pointerdown', 'mouseup', 'touchend', 'keydown', 'keyup', 'keypress', 'submit']);

function isInsideUserEvent(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  // window.event: sự kiện native đang được dispatch (React chạy handler trong đó).
  // Deprecated nhưng mọi trình duyệt chính còn hỗ trợ; thiếu thì coi như không trong sự kiện.
  const event = (window as any).event as Event | undefined;
  return !!event && event.isTrusted && USER_EVENT_TYPES.has(event.type);
}

/**
 * push/replace giữ nguyên pathname + search (chỉ đổi `state`, vd. overlay tìm kiếm mobile đẩy một
 * entry giả để nút back đóng overlay): không có trang mới để render, nên chạy đồng bộ như cũ.
 * Code gọi loại push này thường đọc `history.location` ngay sau đó (MobileSearchOverlayContext
 * lấy `location.key`); hoãn nó thì overlay mở rồi tự đóng.
 */
function isSameUrl(to: unknown): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  // history v4 giải mã pathname (decodeURI), window.location thì không: so sánh ở dạng đã giải mã.
  const decode = (value: string) => {
    try {
      return decodeURI(value);
    } catch (error) {
      return value;
    }
  };
  const pathname = decode(window.location.pathname);
  const search = decode(window.location.search);
  if (typeof to === 'string') {
    try {
      const url = new URL(to, window.location.href);
      return decode(url.pathname) === pathname && decode(url.search) === search;
    } catch (error) {
      return false;
    }
  }
  if (to && typeof to === 'object') {
    const target = to as { pathname?: string; search?: string };
    const rawSearch = target.search ? (target.search.startsWith('?') ? target.search : `?${target.search}`) : '';
    const samePath = target.pathname === undefined || decode(target.pathname) === pathname;
    return samePath && decode(rawSearch) === search;
  }
  return false;
}

function shouldDefer(args: any[]): boolean {
  return isInsideUserEvent() && !isSameUrl(args[0]);
}

function afterNextPaint(fn: () => void): void {
  if (typeof requestAnimationFrame !== 'function' || document.visibilityState !== 'visible') {
    // Tab ẩn thì rAF không chạy — đừng giữ điều hướng lại.
    setTimeout(fn, 0);
    return;
  }
  requestAnimationFrame(() => {
    setTimeout(fn, 0);
  });
}

export { isSameUrl };

export default function yieldBeforeNavigation<H extends NavigableHistory>(history: H): H {
  const originalPush = history.push.bind(history);
  const originalReplace = history.replace.bind(history);

  history.push = (...args: any[]) => {
    if (!shouldDefer(args)) {
      originalPush(...args);
      return;
    }
    afterNextPaint(() => originalPush(...args));
  };

  history.replace = (...args: any[]) => {
    if (!shouldDefer(args)) {
      originalReplace(...args);
      return;
    }
    afterNextPaint(() => originalReplace(...args));
  };

  return history;
}
